import {Injectable} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {firstValueFrom, map} from 'rxjs';
import {PaginatorService} from "@shared/api/services/paginator.service";
import {Pagination, PaginationResponse} from '@models/pagination.model';
import {
    Organisation,
    OrganisationAdminListItem,
    OrganisationCareProgram,
    OrganisationDetail,
    OrganisationLinkedContract,
    OrganisationLinkedQuery,
    OrganisationLinkedService,
    OrganisationListFilters,
    OrganisationListItem,
    OrganisationLogEntry
} from "@shared/api/models/query/organisation.model";
import {
    OrganisationAdminsAdd,
    OrganisationCareProgramsAdd,
    OrganisationContractsAdd,
    OrganisationCreate,
    OrganisationDeactivate,
    OrganisationDelete,
    OrganisationServicesAdd,
    OrganisationUpdate
} from '@shared/api/models/command/organisation.model';
import {AdminUserLookupItem} from '@shared/api/models/query/contract.model';
import {listAdminUsersByRole, paginationParams, responseBody} from '@shared/api/services/v2/api-request';

type OrganisationId = { data: { id: number } };

/**
 * UNIVAPLAT-3689: integration layer for the admin organisation endpoints
 * (`/api/v2/organisations`, backend contract UNIVAPLAT-3665).
 *
 * Promise-based per codebase convention; components never call HttpClient. Every call goes
 * through `responseBody` with `observe: 'events'` because the global interceptor swallows
 * 401/403/404/500 into a string. Errors (403, 404, 409, 422) propagate to the caller untouched:
 * the backend owns the user-facing text. The service holds no admin, log or reason data between
 * calls and never logs any of it.
 *
 * The inherited `listAll` and `listPaginated` keep serving the admin dashboard filter and
 * patient registration (`Organisation`: `id`, `name`).
 */
@Injectable({
    providedIn: 'root'
})
export class OrganisationsService extends PaginatorService<Organisation> {

    protected override get domainEntity() {
        return 'organisations';
    };

    /** `GET /api/v2/organisations`. `showDeactivated` and `showDeleted` are sent only when true. */
    async list(pagination: Pagination = new Pagination(), filters: OrganisationListFilters = {}): Promise<PaginationResponse<OrganisationListItem>> {
        let params = paginationParams(pagination);
        for (const field of ['name', 'code', 'address', 'city', 'postcode'] as const) {
            if (filters[field]) {
                params = params.set(field, filters[field]);
            }
        }
        if (filters.showDeactivated) {
            params = params.set('showDeactivated', 'true');
        }
        if (filters.showDeleted) {
            params = params.set('showDeleted', 'true');
        }
        return firstValueFrom(this.http.get<PaginationResponse<OrganisationListItem>>(this.urlToEntity, {params, observe: 'events'})
            .pipe(responseBody(), map((response) => ({
                data: response?.data ?? [],
                pagination: response?.pagination
            }))));
    }

    /** `GET /api/v2/organisations/{id}`. Errors: 403, 404. */
    async get(id: number): Promise<OrganisationDetail> {
        return firstValueFrom(this.http.get<{ data: OrganisationDetail }>(`${this.urlToEntity}/${id}`, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `POST /api/v2/organisations`. Errors: 403, 422 (`organisation_code_already_exists`). */
    async create(body: OrganisationCreate): Promise<{ id: number }> {
        return firstValueFrom(this.http.post<OrganisationId>(this.urlToEntity, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `PUT /api/v2/organisations/{id}`. Errors: 403, 404, 422 (not active, or code already exists). */
    async update(id: number, body: OrganisationUpdate): Promise<{ id: number }> {
        return firstValueFrom(this.http.put<OrganisationId>(`${this.urlToEntity}/${id}`, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /**
     * `DELETE /api/v2/organisations/{id}` with an optional JSON reason (soft delete; answers the id).
     * Errors: 403, 404, 422 (`organisation_linked_to_contracts`, `organisation_linked_to_services`).
     */
    async delete(id: number, reason?: string): Promise<{ id: number }> {
        const body: OrganisationDelete = reason !== undefined ? {reason} : {};
        return firstValueFrom(this.http.delete<OrganisationId>(`${this.urlToEntity}/${id}`, {body, observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `PATCH /api/v2/organisations/{id}/deactivate`, optional reason. Errors: 403, 404, 409, 422. */
    async deactivate(id: number, reason?: string): Promise<{ id: number }> {
        const body: OrganisationDeactivate = reason !== undefined ? {reason} : {};
        return firstValueFrom(this.http.patch<OrganisationId>(`${this.urlToEntity}/${id}/deactivate`, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `PATCH /api/v2/organisations/{id}/reactivate`, no body. Errors: 403, 404, 409, 422. */
    async reactivate(id: number): Promise<{ id: number }> {
        return firstValueFrom(this.http.patch<OrganisationId>(`${this.urlToEntity}/${id}/reactivate`, null, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `GET /api/v2/organisations/{id}/logs`, newest first unless a sort is given. General admin only. */
    async listLogs(id: number, pagination: Pagination = new Pagination()): Promise<PaginationResponse<OrganisationLogEntry>> {
        return firstValueFrom(this.http.get<PaginationResponse<OrganisationLogEntry>>(`${this.urlToEntity}/${id}/logs`, {params: paginationParams(pagination), observe: 'events'})
            .pipe(responseBody(), map((response) => ({
                data: response?.data ?? [],
                pagination: response?.pagination
            }))));
    }

    /** `GET /api/v2/organisations/{id}/admins`, flat `{data}`. Errors: 403, 404, 422 (organisation not active). */
    async listAdmins(id: number): Promise<OrganisationAdminListItem[]> {
        return this.listFlat<OrganisationAdminListItem>(`${this.urlToEntity}/${id}/admins`);
    }

    /** `POST /api/v2/organisations/{id}/admins`: user ids. Errors: 400, 403, 404, 422. */
    async addAdmins(id: number, userIds: number[]): Promise<{ id: number }> {
        const body: OrganisationAdminsAdd = {organisationAdmins: userIds};
        return this.postIds(`${this.urlToEntity}/${id}/admins`, body);
    }

    /** `DELETE /api/v2/organisations/{id}/admins/{adminId}`; `adminId` is `OrganisationAdminListItem.id`. */
    async removeAdmin(id: number, adminId: number): Promise<void> {
        await this.remove(`${this.urlToEntity}/${id}/admins/${adminId}`);
    }

    /**
     * `GET /api/v2/users/admins?role=admin-org` (general admin only on the backend): every enabled
     * Admin Org user, offered by the "link organisation admins" popup.
     */
    async listCandidateAdmins(): Promise<AdminUserLookupItem[]> {
        return listAdminUsersByRole(this.http, 'admin-org');
    }

    /** `GET /api/v2/organisations/{id}/contracts`, flat `{data}` (no `linked` filter on this endpoint). */
    async listContracts(id: number): Promise<OrganisationLinkedContract[]> {
        return this.listFlat<OrganisationLinkedContract>(`${this.urlToEntity}/${id}/contracts`);
    }

    /** `POST /api/v2/organisations/{id}/contracts` once with every id. Errors: 400, 403, 404, 422. */
    async addContracts(id: number, contractIds: number[]): Promise<{ id: number }> {
        const body: OrganisationContractsAdd = {contractIds};
        return this.postIds(`${this.urlToEntity}/${id}/contracts`, body);
    }

    /** `DELETE /api/v2/organisations/{id}/contracts/{contractId}`; answers 204. */
    async removeContract(id: number, contractId: number): Promise<void> {
        await this.remove(`${this.urlToEntity}/${id}/contracts/${contractId}`);
    }

    /** `GET /api/v2/organisations/{id}/care-programs`; `linked: false` lists the catalogue entries not yet linked. */
    async listCareProgrammes(id: number, query: OrganisationLinkedQuery = {}): Promise<OrganisationCareProgram[]> {
        return this.listFlat<OrganisationCareProgram>(`${this.urlToEntity}/${id}/care-programs`, this.linkedParams(query));
    }

    /** `POST /api/v2/organisations/{id}/care-programs` once with every id. Errors: 400, 403, 404, 422. */
    async addCareProgrammes(id: number, careProgramIds: number[]): Promise<{ id: number }> {
        const body: OrganisationCareProgramsAdd = {careProgramIds};
        return this.postIds(`${this.urlToEntity}/${id}/care-programs`, body);
    }

    /** `DELETE /api/v2/organisations/{id}/care-programs/{careProgramId}`; answers 204. */
    async removeCareProgramme(id: number, careProgramId: number): Promise<void> {
        await this.remove(`${this.urlToEntity}/${id}/care-programs/${careProgramId}`);
    }

    /** `GET /api/v2/organisations/{id}/services`; `linked: false` lists the active services not yet linked. */
    async listServices(id: number, query: OrganisationLinkedQuery = {}): Promise<OrganisationLinkedService[]> {
        return this.listFlat<OrganisationLinkedService>(`${this.urlToEntity}/${id}/services`, this.linkedParams(query));
    }

    /** `POST /api/v2/organisations/{id}/services` once with every id. Errors: 400, 403, 404, 422. */
    async addServices(id: number, serviceIds: number[]): Promise<{ id: number }> {
        const body: OrganisationServicesAdd = {serviceIds};
        return this.postIds(`${this.urlToEntity}/${id}/services`, body);
    }

    /** `DELETE /api/v2/organisations/{id}/services/{serviceId}`; answers 204. */
    async removeService(id: number, serviceId: number): Promise<void> {
        await this.remove(`${this.urlToEntity}/${id}/services/${serviceId}`);
    }

    /** `linked` is sent only when given, as `true` or `false`. */
    private linkedParams(query: OrganisationLinkedQuery): HttpParams {
        let params = new HttpParams();
        if (query.linked !== undefined) {
            params = params.set('linked', query.linked ? 'true' : 'false');
        }
        return params;
    }

    private async listFlat<T>(url: string, params: HttpParams = new HttpParams()): Promise<T[]> {
        return firstValueFrom(this.http.get<{ data: T[] }>(url, {params, observe: 'events'})
            .pipe(responseBody(), map((response) => response?.data ?? [])));
    }

    private async postIds(url: string, body: object): Promise<{ id: number }> {
        return firstValueFrom(this.http.post<OrganisationId>(url, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    private async remove(url: string): Promise<void> {
        await firstValueFrom(this.http.delete<void>(url, {observe: 'events'}).pipe(responseBody()));
    }
}
